import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import '../../pages/Credits.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminCreditWallets = () => {
  const { token } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [walletDetail, setWalletDetail] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchWallets = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const authToken = token || localStorage.getItem('marilink_token');
      const qs = new URLSearchParams({ page, limit: 20 });
      if (search) qs.append('search', search);
      const res = await fetch(`${API_BASE}/admin/credits/wallets?${qs}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.success) {
        setWallets(data.wallets || []);
        setPagination(data.pagination);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, search]);

  const fetchStats = useCallback(async () => {
    try {
      const authToken = token || localStorage.getItem('marilink_token');
      const res = await fetch(`${API_BASE}/admin/credits/stats`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.success) setStats(data.stats);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchWallets(1); fetchStats(); }, [fetchWallets, fetchStats]);

  const handleOpenUser = async (userId) => {
    setSelectedUser(userId);
    setWalletDetail(null);
    try {
      const authToken = token || localStorage.getItem('marilink_token');
      const res = await fetch(`${API_BASE}/admin/credits/wallets/${userId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.success) setWalletDetail(data);
    } catch (e) { console.error(e); }
  };

  const handleAdjust = async () => {
    if (!selectedUser) return;
    const amt = parseInt(adjustAmount, 10);
    if (!Number.isInteger(amt) || amt === 0) { alert('Enter a non-zero integer'); return; }
    if (!adjustReason.trim()) { alert('Reason is required'); return; }
    setProcessing(true);
    try {
      const authToken = token || localStorage.getItem('marilink_token');
      const res = await fetch(`${API_BASE}/admin/credits/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ userId: selectedUser, credits: amt, reason: adjustReason })
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ ${amt > 0 ? 'Added' : 'Deducted'} ${Math.abs(amt)} credits`);
        setAdjustAmount('');
        setAdjustReason('');
        handleOpenUser(selectedUser);
        fetchWallets(pagination.page);
        fetchStats();
      } else {
        alert(data.message || 'Failed');
      }
    } catch (e) { alert('Network error'); }
    finally { setProcessing(false); }
  };

  return (
    <div className="cr-page">
      <div className="cr-header">
        <div>
          <h1><i className="fas fa-users-cog"></i> Credit Wallets</h1>
          <p>Manage all client credit wallets and adjustments</p>
        </div>
      </div>

      {stats && (
        <div className="cr-balance-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <div className="cr-stat-card">
            <span className="label">Total Balance</span>
            <span className="value green">{stats.totalBalance?.toLocaleString()}</span>
            <span className="cr-subtle">credits</span>
          </div>
          <div className="cr-stat-card">
            <span className="label">Total Purchased</span>
            <span className="value">{stats.totalPurchased?.toLocaleString()}</span>
            <span className="cr-subtle">credits</span>
          </div>
          <div className="cr-stat-card">
            <span className="label">Total Used</span>
            <span className="value">{stats.totalUsed?.toLocaleString()}</span>
            <span className="cr-subtle">credits</span>
          </div>
          <div className="cr-stat-card">
            <span className="label">Active Wallets</span>
            <span className="value">{stats.activeWallets || 0}</span>
            <span className="cr-subtle">of {stats.totalWallets || 0}</span>
          </div>
        </div>
      )}

      <div className="cr-filters">
        <input
          type="text"
          placeholder="Search by name, email, or company..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 280 }}
        />
      </div>

      {loading ? (
        <div className="cr-loading"><i className="fas fa-spinner fa-spin"></i></div>
      ) : wallets.length === 0 ? (
        <div className="cr-empty">
          <i className="fas fa-inbox"></i>
          <h3>No wallets found</h3>
        </div>
      ) : (
        <div className="cr-table-wrap">
          <table className="cr-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Balance</th>
                <th>Purchased</th>
                <th>Used</th>
                <th>Refunded</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {wallets.map(w => (
                <tr key={w.userId}>
                  <td>
                    <div className="cr-user-cell">
                      <span className="cr-user-name">{w.user?.company || w.user?.name || 'N/A'}</span>
                      <span className="cr-user-email">{w.user?.email}</span>
                    </div>
                  </td>
                  <td><span className="cr-strong-green">{w.balance}</span></td>
                  <td>{w.totalPurchased || 0}</td>
                  <td>{w.totalUsed || 0}</td>
                  <td>{w.totalRefunded || 0}</td>
                  <td>
                    <button className="cr-btn cr-btn-secondary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => handleOpenUser(w.userId)}>
                      <i className="fas fa-sliders-h"></i> Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedUser && (
        <div className="cr-modal-overlay" onClick={() => { setSelectedUser(null); setWalletDetail(null); }}>
          <div className="cr-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <h3><i className="fas fa-user-cog"></i> Wallet Management</h3>
            {!walletDetail ? (
              <div className="cr-loading"><i className="fas fa-spinner fa-spin"></i></div>
            ) : (
              <>
                <p className="cr-modal-sub">
                  {walletDetail.user?.company || walletDetail.user?.name} — {walletDetail.user?.email}
                </p>

                <div className="cr-summary">
                  <div className="cr-summary-row"><span>Balance</span><span className="cr-strong-green">{walletDetail.wallet.balance} credits</span></div>
                  <div className="cr-summary-row"><span>Total Purchased</span><span>{walletDetail.wallet.totalPurchased}</span></div>
                  <div className="cr-summary-row"><span>Total Used</span><span>{walletDetail.wallet.totalUsed}</span></div>
                  <div className="cr-summary-row"><span>Total Refunded</span><span>{walletDetail.wallet.totalRefunded}</span></div>
                </div>

                <div className="cr-adjust-panel">
                  <h4>Manual Adjustment</h4>
                  <div className="cr-form-group">
                    <label>Amount (positive to add, negative to deduct)</label>
                    <input type="number" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} placeholder="e.g. 100 or -50" />
                  </div>
                  <div className="cr-form-group">
                    <label>Reason *</label>
                    <input type="text" value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Reason for adjustment" />
                  </div>
                  <button className="cr-btn cr-btn-primary" onClick={handleAdjust} disabled={processing}>
                    {processing ? <><i className="fas fa-spinner fa-spin"></i> Processing...</> : <><i className="fas fa-check"></i> Apply Adjustment</>}
                  </button>
                </div>

                <div className="cr-recent-block">
                  <h4>Recent Transactions</h4>
                  {walletDetail.transactions?.slice(0, 8).map(tx => (
                    <div key={tx._id} className="cr-recent-row">
                      <div>
                        <div className="cr-recent-type">{tx.type.replace(/_/g, ' ')}</div>
                        <div className="cr-recent-time">{new Date(tx.createdAt).toLocaleString()}</div>
                      </div>
                      <div className={tx.credits > 0 ? 'cr-tx-positive' : 'cr-tx-negative'}>
                        {tx.credits > 0 ? '+' : ''}{tx.credits}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="cr-modal-actions">
                  <button className="cr-btn cr-btn-secondary" onClick={() => { setSelectedUser(null); setWalletDetail(null); }}>Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCreditWallets;